import { useCallback, useEffect, useMemo, useState } from 'react';
import { Surface, eyebrowStyle, ghostButtonStyle, ghostLinkStyle, pageStyle } from '@kilat/ui';
import { Link } from 'react-router-dom';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { logger } from '../lib/logger.js';
import { PAGE_FRAME_MAX, dashboardPageStyle } from '../lib/layout.js';
import {
  callGetPlatformFinanceSummary,
  type FinancePeriod,
  type GetPlatformFinanceSummaryOutput,
} from '../lib/callables/finance.js';
import { money } from '@kilat/shared';

/**
 * /superadmin/finance — Platform Finance dashboard, slice 1 (roadmap #10).
 *
 * Shows, for the caller's platform brand only:
 *   - Platform income: subscription revenue (selected period + all-time) and
 *     the MRR run-rate. Revenue is the invoice `subtotal` (PRE-tax) — Kilat is
 *     a zero-cut MOR, so this is its only income. SST is surfaced on a
 *     visually-separated muted line, clearly framed as collected on behalf of
 *     the government (a pass-through, NEVER revenue).
 *   - Tenants: operator counts by lifecycle status, with a next-action link to
 *     the operators list.
 *   - Liabilities owed (slice 2): unpaid agent + recruiter commissions, the
 *     combined total, and a "payable now" line (what the next batch settles).
 *   - Payout status (slice 2): pending total (+ open-batch count), paid
 *     all-time, and a pending-by-recipient split. Both link to /superadmin/payouts.
 *
 * Each section reads its own server-side `ok` flag: a failed sub-read renders
 * an inline error + Retry (which re-calls), NEVER a false RM 0.00. A genuine
 * zero renders RM 0.00 / 0 WITH context copy + a next action (no dead-ends).
 *
 * Brand-scoped: the super-admin claim is `{ superadmin: { [brandId]: true } }`
 * (ADR-0017). The callable filters every figure to the active brand id from
 * `usePlatformBrand()`; a brand-A super-admin can never see brand-B money.
 *
 * No floating chrome: the header is in-flow with a "← Back to queue" link,
 * matching /superadmin/payouts (per no-floating-chrome-overlap).
 */

const PERIODS: readonly FinancePeriod[] = ['this_month', 'last_month', 'all_time'];

export function SuperadminFinancePage() {
  const { claims, loading: authLoading } = useAuth();
  const brand = usePlatformBrand();
  const { t } = useI18n();
  const isSuper = useMemo(() => isSuperAdminForBrand(claims, brand.id), [claims, brand.id]);

  if (authLoading) return <Screen title={t('superadmin.finance.loadingTitle')} />;
  if (!isSuper) {
    return (
      <Screen
        title={t('superadmin.finance.guardTitle')}
        body={t('superadmin.finance.guardBody', { brand: brand.name })}
        cta={{ label: t('superadmin.finance.guardCta'), to: '/queue' }}
      />
    );
  }

  return (
    <main style={dashboardPageStyle}>
      <header style={headerStyle}>
        <div style={{ maxWidth: PAGE_FRAME_MAX, margin: '0 auto', padding: 'var(--space-5)' }}>
          <p style={eyebrowStyle}>{t('superadmin.finance.eyebrow')}</p>
          <h1
            style={{ margin: 'var(--space-1) 0 0 0', fontSize: 'var(--text-h1)', fontWeight: 600 }}
          >
            {t('superadmin.finance.title', { brand: brand.name })}
          </h1>
          <p
            style={{
              margin: 'var(--space-2) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              maxWidth: 720,
            }}
          >
            {t('superadmin.finance.subtitle')}
          </p>
          <div style={{ marginTop: 'var(--space-4)' }}>
            <Link to="/queue" style={ghostLinkStyle}>
              {t('superadmin.finance.backToQueue')}
            </Link>
          </div>
        </div>
      </header>

      <section style={contentStyle}>
        <FinancePanel brandId={brand.id} />
      </section>
    </main>
  );
}

// ── Main panel ─────────────────────────────────────────────────────────

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; messageKey: string }
  | { kind: 'ready'; data: GetPlatformFinanceSummaryOutput };

function FinancePanel({ brandId }: { readonly brandId: string }) {
  const { t } = useI18n();
  const [period, setPeriod] = useState<FinancePeriod>('this_month');
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  const reload = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const data = await callGetPlatformFinanceSummary({ brandId, period });
      setState({ kind: 'ready', data });
    } catch (e) {
      logger.error('getPlatformFinanceSummary failed', { error: String(e) });
      setState({ kind: 'error', messageKey: errorKey(e) });
    }
  }, [brandId, period]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)' }}>
      <PeriodSwitcher value={period} onChange={setPeriod} />

      {state.kind === 'error' ? (
        <Surface as="div">
          <InlineNote tone="error">{t(state.messageKey)}</InlineNote>
          <button
            type="button"
            onClick={() => void reload()}
            style={{ ...ghostButtonStyle, marginTop: 'var(--space-3)' }}
          >
            {t('superadmin.finance.errorRetry')}
          </button>
        </Surface>
      ) : state.kind === 'loading' ? (
        <>
          <SkeletonCard label={t('superadmin.finance.income.title')} tiles={3} />
          <SkeletonCard label={t('superadmin.finance.tenants.title')} tiles={5} />
          <SkeletonCard label={t('superadmin.finance.liabilities.title')} tiles={3} />
          <SkeletonCard label={t('superadmin.finance.payouts.title')} tiles={2} />
        </>
      ) : (
        <>
          <IncomeSection data={state.data} period={period} onRetry={() => void reload()} />
          <TenantsSection data={state.data} onRetry={() => void reload()} />
          <LiabilitiesSection data={state.data} onRetry={() => void reload()} />
          <PayoutsSection data={state.data} onRetry={() => void reload()} />
          <p style={updatedAtStyle}>
            {t('superadmin.finance.updatedAt', { time: formatTime(state.data.computedAtMs) })}
          </p>
        </>
      )}
    </div>
  );
}

// ── KPI row 1 — Platform income ────────────────────────────────────────

function IncomeSection({
  data,
  period,
  onRetry,
}: {
  readonly data: GetPlatformFinanceSummaryOutput;
  readonly period: FinancePeriod;
  readonly onRetry: () => void;
}) {
  const { t } = useI18n();
  const { revenue, tenants } = data;
  return (
    <Surface as="section">
      <h2 style={sectionTitleStyle}>{t('superadmin.finance.income.title')}</h2>
      <p style={sectionHintStyle}>{t('superadmin.finance.income.hint')}</p>

      <div style={{ ...tileGridStyle, marginTop: 'var(--space-4)' }}>
        {/* Subscription revenue — selected period */}
        <StatTile
          label={t('superadmin.finance.income.revenuePeriodLabel')}
          sub={t(`superadmin.finance.period.${period}`)}
        >
          {revenue.ok ? (
            revenue.periodSen === 0 ? (
              <ZeroValue
                amount={money.format(0)}
                context={t('superadmin.finance.income.zeroPeriod')}
                linkLabel={t('superadmin.finance.income.viewOperators')}
                to="/superadmin/tenants"
              />
            ) : (
              <MoneyValue amount={money.format(revenue.periodSen)} />
            )
          ) : (
            <TileError
              message={t('superadmin.finance.income.error')}
              retryLabel={t('superadmin.finance.retry')}
              onRetry={onRetry}
            />
          )}
        </StatTile>

        {/* Subscription revenue — all-time */}
        <StatTile
          label={t('superadmin.finance.income.revenueAllTimeLabel')}
          sub={t('superadmin.finance.income.allTimeSub')}
        >
          {revenue.ok ? (
            <MoneyValue
              amount={money.format(revenue.allTimeSen)}
              muted={revenue.allTimeSen === 0}
            />
          ) : (
            <TileError
              message={t('superadmin.finance.income.error')}
              retryLabel={t('superadmin.finance.retry')}
              onRetry={onRetry}
            />
          )}
        </StatTile>

        {/* MRR run-rate — sourced from the tenants section (active operators) */}
        <StatTile
          label={t('superadmin.finance.income.mrrLabel')}
          sub={t('superadmin.finance.income.mrrSub')}
        >
          {tenants.ok ? (
            <MoneyValue amount={money.format(tenants.mrrSen)} muted={tenants.mrrSen === 0} />
          ) : (
            <TileError
              message={t('superadmin.finance.tenants.error')}
              retryLabel={t('superadmin.finance.retry')}
              onRetry={onRetry}
            />
          )}
        </StatTile>
      </div>

      {/* SST — visually separated muted pass-through line, NEVER revenue. */}
      {revenue.ok ? (
        <p style={sstNoteStyle}>
          {t('superadmin.finance.income.sstNote', {
            amount: money.format(revenue.sstPeriodSen),
          })}
        </p>
      ) : null}
    </Surface>
  );
}

// ── KPI row 2 — Tenants ────────────────────────────────────────────────

function TenantsSection({
  data,
  onRetry,
}: {
  readonly data: GetPlatformFinanceSummaryOutput;
  readonly onRetry: () => void;
}) {
  const { t } = useI18n();
  const { tenants } = data;

  return (
    <Surface as="section">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <h2 style={sectionTitleStyle}>{t('superadmin.finance.tenants.title')}</h2>
        <Link to="/superadmin/tenants" style={ghostLinkStyle}>
          {t('superadmin.finance.tenants.manageLink')}
        </Link>
      </div>
      <p style={sectionHintStyle}>{t('superadmin.finance.tenants.hint')}</p>

      {!tenants.ok ? (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <InlineNote tone="error">{t('superadmin.finance.tenants.error')}</InlineNote>
          <button
            type="button"
            onClick={onRetry}
            style={{ ...ghostButtonStyle, marginTop: 'var(--space-3)' }}
          >
            {t('superadmin.finance.retry')}
          </button>
        </div>
      ) : isTenantsEmpty(tenants) ? (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <InlineNote>{t('superadmin.finance.tenants.zero')}</InlineNote>
          <Link
            to="/superadmin/tenants"
            style={{ ...ghostLinkStyle, display: 'inline-block', marginTop: 'var(--space-3)' }}
          >
            {t('superadmin.finance.tenants.zeroCta')}
          </Link>
        </div>
      ) : (
        <div style={{ ...tileGridStyle, marginTop: 'var(--space-4)' }}>
          <CountTile label={t('superadmin.finance.tenants.active')} value={tenants.active} accent />
          <CountTile label={t('superadmin.finance.tenants.trial')} value={tenants.trial} />
          <CountTile label={t('superadmin.finance.tenants.pastDue')} value={tenants.pastDue} />
          <CountTile label={t('superadmin.finance.tenants.suspended')} value={tenants.suspended} />
          <CountTile label={t('superadmin.finance.tenants.archived')} value={tenants.archived} />
        </div>
      )}
    </Surface>
  );
}

function isTenantsEmpty(t: {
  readonly active: number;
  readonly trial: number;
  readonly pastDue: number;
  readonly suspended: number;
  readonly archived: number;
}): boolean {
  return t.active + t.trial + t.pastDue + t.suspended + t.archived === 0;
}

// ── KPI row 3 — Liabilities owed (slice 2) ─────────────────────────────

function LiabilitiesSection({
  data,
  onRetry,
}: {
  readonly data: GetPlatformFinanceSummaryOutput;
  readonly onRetry: () => void;
}) {
  const { t } = useI18n();
  const { liabilities } = data;

  return (
    <Surface as="section">
      <div style={sectionHeaderRowStyle}>
        <h2 style={sectionTitleStyle}>{t('superadmin.finance.liabilities.title')}</h2>
        <Link to="/superadmin/payouts" style={ghostLinkStyle}>
          {t('superadmin.finance.liabilities.cta')}
        </Link>
      </div>
      <p style={sectionHintStyle}>{t('superadmin.finance.liabilities.hint')}</p>

      {!liabilities.ok ? (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <InlineNote tone="error">{t('superadmin.finance.liabilities.error')}</InlineNote>
          <button
            type="button"
            onClick={onRetry}
            style={{ ...ghostButtonStyle, marginTop: 'var(--space-3)' }}
          >
            {t('superadmin.finance.retry')}
          </button>
        </div>
      ) : liabilities.agentsUnpaidSen + liabilities.recruitersUnpaidSen === 0 ? (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <InlineNote>{t('superadmin.finance.liabilities.zero')}</InlineNote>
          <Link
            to="/superadmin/payouts"
            style={{ ...ghostLinkStyle, display: 'inline-block', marginTop: 'var(--space-3)' }}
          >
            {t('superadmin.finance.liabilities.zeroCta')}
          </Link>
        </div>
      ) : (
        <>
          <div style={{ ...tileGridStyle, marginTop: 'var(--space-4)' }}>
            <StatTile label={t('superadmin.finance.liabilities.agentsLabel')}>
              <MoneyValue
                amount={money.format(liabilities.agentsUnpaidSen)}
                muted={liabilities.agentsUnpaidSen === 0}
              />
            </StatTile>
            <StatTile label={t('superadmin.finance.liabilities.recruitersLabel')}>
              <MoneyValue
                amount={money.format(liabilities.recruitersUnpaidSen)}
                muted={liabilities.recruitersUnpaidSen === 0}
              />
            </StatTile>
            <StatTile
              label={t('superadmin.finance.liabilities.totalLabel')}
              sub={t('superadmin.finance.liabilities.totalSub')}
            >
              <MoneyValue
                amount={money.format(liabilities.agentsUnpaidSen + liabilities.recruitersUnpaidSen)}
              />
            </StatTile>
          </div>
          {/* "Payable now" — of the total owed, what a batch run settles today. */}
          <p style={sstNoteStyle}>
            {t('superadmin.finance.liabilities.payableNote', {
              agents: money.format(liabilities.agentsPayableNowSen),
              recruiters: money.format(liabilities.recruitersPayableNowSen),
            })}
          </p>
        </>
      )}
    </Surface>
  );
}

// ── KPI row 4 — Payout status (slice 2) ────────────────────────────────

function PayoutsSection({
  data,
  onRetry,
}: {
  readonly data: GetPlatformFinanceSummaryOutput;
  readonly onRetry: () => void;
}) {
  const { t } = useI18n();
  const { payouts } = data;

  return (
    <Surface as="section">
      <div style={sectionHeaderRowStyle}>
        <h2 style={sectionTitleStyle}>{t('superadmin.finance.payouts.title')}</h2>
        <Link to="/superadmin/payouts" style={ghostLinkStyle}>
          {t('superadmin.finance.payouts.cta')}
        </Link>
      </div>
      <p style={sectionHintStyle}>{t('superadmin.finance.payouts.hint')}</p>

      {!payouts.ok ? (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <InlineNote tone="error">{t('superadmin.finance.payouts.error')}</InlineNote>
          <button
            type="button"
            onClick={onRetry}
            style={{ ...ghostButtonStyle, marginTop: 'var(--space-3)' }}
          >
            {t('superadmin.finance.retry')}
          </button>
        </div>
      ) : payouts.pendingSen === 0 && payouts.paidAllTimeSen === 0 ? (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <InlineNote>{t('superadmin.finance.payouts.zero')}</InlineNote>
          <Link
            to="/superadmin/payouts"
            style={{ ...ghostLinkStyle, display: 'inline-block', marginTop: 'var(--space-3)' }}
          >
            {t('superadmin.finance.payouts.zeroCta')}
          </Link>
        </div>
      ) : (
        <>
          <div style={{ ...tileGridStyle, marginTop: 'var(--space-4)' }}>
            <StatTile
              label={t('superadmin.finance.payouts.pendingLabel')}
              sub={pendingSub(t, payouts.pendingSen, payouts.pendingCount)}
            >
              <MoneyValue
                amount={money.format(payouts.pendingSen)}
                muted={payouts.pendingSen === 0}
              />
            </StatTile>
            <StatTile
              label={t('superadmin.finance.payouts.paidLabel')}
              sub={t('superadmin.finance.payouts.paidSub')}
            >
              <MoneyValue
                amount={money.format(payouts.paidAllTimeSen)}
                muted={payouts.paidAllTimeSen === 0}
              />
            </StatTile>
          </div>
          {/* Pending-by-recipient split — only meaningful while something is pending. */}
          {payouts.pendingSen > 0 ? (
            <p style={sstNoteStyle}>
              {t('superadmin.finance.payouts.byKindNote', {
                agent: money.format(payouts.byKind.agent.pendingSen),
                recruiter: money.format(payouts.byKind.recruiter.pendingSen),
                tenant: money.format(payouts.byKind.tenant.pendingSen),
              })}
            </p>
          ) : null}
        </>
      )}
    </Surface>
  );
}

/** Concise uppercase sub-label for the pending-payouts tile. */
function pendingSub(
  t: (key: string, vars?: Record<string, string | number>) => string,
  pendingSen: number,
  pendingCount: number,
): string {
  if (pendingSen === 0) return t('superadmin.finance.payouts.pendingNone');
  if (pendingCount === 1) return t('superadmin.finance.payouts.pendingCountOne');
  return t('superadmin.finance.payouts.pendingCountOther', { count: pendingCount });
}

// ── Value + tile primitives ────────────────────────────────────────────

function StatTile({
  label,
  sub,
  children,
}: {
  readonly label: string;
  readonly sub?: string;
  readonly children: React.ReactNode;
}) {
  return (
    <div style={tileStyle}>
      <p style={tileLabelStyle}>{label}</p>
      {sub ? <p style={tileSubStyle}>{sub}</p> : null}
      <div style={{ marginTop: 'var(--space-3)' }}>{children}</div>
    </div>
  );
}

function MoneyValue({ amount, muted }: { readonly amount: string; readonly muted?: boolean }) {
  return (
    <span
      style={{
        fontSize: 'var(--text-h1)',
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        color: muted ? 'var(--color-on-surface-muted)' : 'var(--color-on-surface)',
      }}
    >
      {amount}
    </span>
  );
}

function ZeroValue({
  amount,
  context,
  linkLabel,
  to,
}: {
  readonly amount: string;
  readonly context: string;
  readonly linkLabel: string;
  readonly to: string;
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <MoneyValue amount={amount} muted />
      <p
        style={{ margin: 0, fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}
      >
        {context}
      </p>
      <Link to={to} style={ghostLinkStyle}>
        {linkLabel}
      </Link>
    </div>
  );
}

function CountTile({
  label,
  value,
  accent,
}: {
  readonly label: string;
  readonly value: number;
  readonly accent?: boolean;
}) {
  return (
    <div style={tileStyle}>
      <p style={tileLabelStyle}>{label}</p>
      <div style={{ marginTop: 'var(--space-3)' }}>
        <span
          style={{
            fontSize: 'var(--text-h1)',
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            color: accent && value > 0 ? 'var(--color-primary)' : 'var(--color-on-surface)',
          }}
        >
          {value}
        </span>
      </div>
    </div>
  );
}

function TileError({
  message,
  retryLabel,
  onRetry,
}: {
  readonly message: string;
  readonly retryLabel: string;
  readonly onRetry: () => void;
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <p role="alert" style={{ margin: 0, fontSize: 'var(--text-meta)', color: '#991b1b' }}>
        {message}
      </p>
      <button
        type="button"
        onClick={onRetry}
        style={{ ...ghostButtonStyle, justifySelf: 'start', padding: '6px 12px' }}
      >
        {retryLabel}
      </button>
    </div>
  );
}

// ── Period switcher ────────────────────────────────────────────────────

function PeriodSwitcher({
  value,
  onChange,
}: {
  readonly value: FinancePeriod;
  readonly onChange: (v: FinancePeriod) => void;
}) {
  const { t } = useI18n();
  return (
    <div
      role="tablist"
      aria-label={t('superadmin.finance.period.label')}
      style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}
    >
      {PERIODS.map((p) => {
        const sel = p === value;
        return (
          <button
            key={p}
            type="button"
            role="tab"
            aria-selected={sel}
            onClick={() => onChange(p)}
            style={{
              padding: '8px 16px',
              borderRadius: 'var(--radius-pill)',
              border: '1px solid var(--color-border-strong)',
              background: sel ? 'var(--color-primary)' : 'var(--color-surface)',
              color: sel ? 'var(--color-primary-fg)' : 'var(--color-on-surface)',
              fontSize: 'var(--text-meta)',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {t(`superadmin.finance.period.${p}`)}
          </button>
        );
      })}
    </div>
  );
}

// ── Skeleton / states ──────────────────────────────────────────────────

function SkeletonCard({ label, tiles }: { readonly label: string; readonly tiles: number }) {
  return (
    <Surface as="section">
      <h2 style={sectionTitleStyle}>{label}</h2>
      <div style={{ ...tileGridStyle, marginTop: 'var(--space-4)' }}>
        {Array.from({ length: tiles }).map((_, i) => (
          <div
            key={i}
            style={{
              height: 96,
              borderRadius: 12,
              background: 'var(--tint-subtle)',
              animation: 'kilatPulse 1.2s ease-in-out infinite',
            }}
          />
        ))}
      </div>
      <style>{`@keyframes kilatPulse { 0%, 100% { opacity: 0.5; } 50% { opacity: 1; } }`}</style>
    </Surface>
  );
}

function InlineNote({
  children,
  tone = 'muted',
}: {
  readonly children: React.ReactNode;
  readonly tone?: 'muted' | 'error';
}) {
  return (
    <p
      role={tone === 'error' ? 'alert' : undefined}
      style={{
        margin: 0,
        padding: '12px 14px',
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--text-meta)',
        background: tone === 'error' ? 'rgba(239,68,68,0.06)' : 'var(--tint-subtle)',
        border: `1px solid ${tone === 'error' ? 'rgba(239,68,68,0.20)' : 'var(--color-border)'}`,
        color: tone === 'error' ? '#991b1b' : 'var(--color-on-surface-muted)',
      }}
    >
      {children}
    </p>
  );
}

function Screen({
  title,
  body,
  cta,
}: {
  readonly title: string;
  readonly body?: string;
  readonly cta?: { label: string; to: string };
}) {
  return (
    <main style={{ ...pageStyle, display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center', maxWidth: 480, padding: 'var(--space-6)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h1>
        {body ? (
          <p
            style={{
              margin: 'var(--space-3) 0 0 0',
              color: 'var(--color-on-surface-muted)',
              lineHeight: 1.5,
            }}
          >
            {body}
          </p>
        ) : null}
        {cta ? (
          <Link
            to={cta.to}
            style={{
              display: 'inline-block',
              marginTop: 'var(--space-4)',
              padding: '12px 22px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-primary)',
              color: 'var(--color-primary-fg)',
              fontWeight: 500,
              textDecoration: 'none',
            }}
          >
            {cta.label}
          </Link>
        ) : null}
      </div>
    </main>
  );
}

// ── helpers ────────────────────────────────────────────────────────────

function formatTime(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Map a callable failure to an i18n KEY (translated by the caller so no raw
 * server string ever leaks to a non-en super-admin). Firebase callable errors
 * carry the code on `err.code` — e.g. 'functions/permission-denied' or
 * 'permission-denied' — NOT inside the message, so we match on `code`.
 * Everything else is a generic retryable error.
 */
function errorKey(err: unknown): string {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = String((err as { code: unknown }).code);
    if (code === 'permission-denied' || code === 'functions/permission-denied') {
      return 'superadmin.finance.errPermission';
    }
  }
  return 'superadmin.finance.errGeneric';
}

function isSuperAdminForBrand(claims: Record<string, unknown> | null, brandId: string): boolean {
  if (!claims) return false;
  const sa = (claims as { superadmin?: Record<string, unknown> }).superadmin;
  if (!sa || typeof sa !== 'object') return false;
  return sa[brandId] === true;
}

// ── styles (mirrors superadmin-payouts.tsx) ────────────────────────────

const headerStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  borderBottom: '1px solid var(--color-border)',
};

const contentStyle: React.CSSProperties = {
  maxWidth: PAGE_FRAME_MAX,
  margin: '0 auto',
  padding: 'var(--space-5)',
};

const tileGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
  gap: 'var(--space-4)',
};

const tileStyle: React.CSSProperties = {
  padding: 'var(--space-4)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-border)',
  background: 'var(--color-surface)',
};

const tileLabelStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  color: 'var(--color-on-surface-muted)',
};

const tileSubStyle: React.CSSProperties = {
  margin: 'var(--space-1) 0 0 0',
  fontSize: '0.6875rem',
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
  color: 'var(--color-on-surface-muted)',
};

const sstNoteStyle: React.CSSProperties = {
  margin: 'var(--space-4) 0 0 0',
  paddingTop: 'var(--space-3)',
  borderTop: '1px dashed var(--color-border-strong)',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontVariantNumeric: 'tabular-nums',
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-h2)',
  fontWeight: 600,
};

const sectionHintStyle: React.CSSProperties = {
  margin: 'var(--space-2) 0 0 0',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  lineHeight: 1.5,
};

const sectionHeaderRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 'var(--space-3)',
  flexWrap: 'wrap',
};

const updatedAtStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  textAlign: 'right',
};
